import { useCallback } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { spliceErefIntoCache } from "helpers/quotationCache.helper"
import { enqueueMutation, getQueueVersionRef } from "store/mutationQueue.store"
import AnnotationService from "services/annotations.service"
import { erefQueueKey } from "../ReferenceSidebar/erefQueueKey"
import { buildErefStatusToast } from "../quotationViewer.helpers"
import type { useUpdateErefStatus } from "hooks/api/quotation/quotations.hook"
import type { TEref, TErefStatus, TErefWithFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

// REQ-373 eref state change from the floating toolbar / context menu. Same flow the eref sidebar
// footer already uses (see ReferenceSidebar.updateStatus): PATCH the status endpoint with the
// eref's current version, splice the returned eref into the quotation cache, and recolor its
// highlight. Two things are deliberate:
//
//   • It runs through the eref's own mutation queue (`erefQueueKey`), which the sidebar and the
//     coordinate commits share, and reads the version from that queue's ref — so the toolbar and
//     the sidebar can act on the same eref without either sending a stale optimistic-lock version.
//   • NO cascade: the endpoint returns the updated eref only, so nothing here touches the connected
//     furniture's own statuses.
export type ErefStatusChangeContext = {
	numericQuotationIdRef: MutableRefObject<number | undefined>
	setBatchPending: (value: boolean) => void
	updateErefStatusAsync: ReturnType<typeof useUpdateErefStatus>["mutateAsync"]
	queryClient: QueryClient
	success: (text: string) => void
	formatError: (error: unknown) => void
}

export const useErefStatusChange = (ctx: ErefStatusChangeContext) => {
	const { numericQuotationIdRef, setBatchPending, updateErefStatusAsync, queryClient, success, formatError } = ctx

	const runErefStatus = useCallback(
		async (eref: TErefWithFurniture, next: TErefStatus) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) return
			const current = eref.status ?? "work_in_progress"
			if (current === next) return
			const key = erefQueueKey(eref.id)
			const versionRef = getQueueVersionRef(key, eref.version)
			setBatchPending(true)
			// Failures are handled INSIDE the queued task (as the sidebar's mutations do), so the
			// queue's promise chain never sees a rejection.
			await enqueueMutation(key, eref.version, async () => {
				try {
					const resp = await updateErefStatusAsync({
						quotationId: qid,
						erefId: eref.id,
						body: { version: versionRef.current, status: next },
					})
					const updated = resp?.data as TEref | undefined
					if (updated) {
						if (typeof updated.version === "number") versionRef.current = updated.version
						queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) =>
							spliceErefIntoCache(cached, updated),
						)
					}
					AnnotationService.updateErefEntityStatus(eref.id, next)
					success(buildErefStatusToast(current, next))
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
				} finally {
					setBatchPending(false)
				}
			})
		},
		[numericQuotationIdRef, setBatchPending, updateErefStatusAsync, queryClient, success, formatError],
	)

	return { runErefStatus }
}
